import React, { useState } from 'react';
import { DuplicateBudgetDialog } from './DuplicateBudgetDialog';
import { useDuplicateBudget } from '../hooks/useBudgetQueries';
import { Budget } from '../types/budget';
import { useTranslation } from 'react-i18next';
import { useToast } from '../contexts/ToastContext';

interface DuplicateBudgetButtonProps {
  budget: Budget & { id: number };
  showText?: boolean;
  className?: string;
  onSuccess?: () => void;
}

export const DuplicateBudgetButton: React.FC<DuplicateBudgetButtonProps> = ({
  budget,
  showText = true,
  className = '',
  onSuccess,
}) => {
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const duplicateBudgetMutation = useDuplicateBudget();
  const { t } = useTranslation();
  const { showToast } = useToast();

  const handleDuplicateConfirm = (name: string) => {
    duplicateBudgetMutation.mutate(
      {
        budgetId: budget.id,
        newName: name,
      },
      {
        onSuccess: () => {
          // Close the dialog immediately
          setDuplicateDialogOpen(false);

          // Show success toast notification
          showToast(t('budget.duplicateSuccess', { name }), 'success');

          // Call onSuccess callback if provided
          if (onSuccess) {
            onSuccess();
          }
        },
        onError: (error) => {
          console.error('Error duplicating budget:', error);
          setDuplicateDialogOpen(false);
          showToast(t('budget.duplicateError'), 'error');
        },
      }
    );
  };

  const baseClasses =
    'flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:bg-indigo-500 dark:text-white dark:hover:bg-indigo-400';
  const buttonClasses = className ? `${baseClasses} ${className}` : baseClasses;

  return (
    <>
      <button
        onClick={() => setDuplicateDialogOpen(true)}
        data-attr="duplicate-budget-button"
        className={buttonClasses}
        title={t('budget.duplicate')}
      >
        {showText && <span>{t('budget.duplicate')}</span>}
      </button>

      <DuplicateBudgetDialog
        isOpen={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        onConfirm={handleDuplicateConfirm}
        defaultName={`${budget.name} - ${t('common.copy')}`}
      />
    </>
  );
};
